import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentOrganization } from '../../../hooks/useOrganizationFeatures';
import type { Organization } from '../../../types/shared';
import {
  availabilityExceptionService,
  availabilityRuleService,
  bookingService,
  resourceService,
  schedulingProfileService,
  serviceService,
  serviceVariantService,
} from './schedulingService';
import {
  AvailabilityExceptionInput,
  AvailabilityRuleInput,
  BookingCreateInput,
  BookingListQuery,
  BookingMoveInput,
  ResourceInput,
  SchedulingProfileInput,
  ServiceInput,
  ServiceVariantInput,
} from './types';

export const schedulingKeys = {
  all: ['scheduling'] as const,
  profiles: ['scheduling', 'profiles'] as const,
  profile: (id: string) => ['scheduling', 'profiles', id] as const,
  bookings: (profileId: string, from: string, to: string) =>
    ['scheduling', 'bookings', profileId, from, to] as const,
  resources: (profileId: string) => ['scheduling', 'resources', profileId] as const,
  services: (profileId: string) => ['scheduling', 'services', profileId] as const,
  rules: (owner: 'profile' | 'resource', id: string) => ['scheduling', 'rules', owner, id] as const,
  exceptions: (owner: 'profile' | 'resource', id: string) =>
    ['scheduling', 'exceptions', owner, id] as const,
};

const selectSchedulingAccess = (organization: Organization | null) => ({
  enabled: Boolean(organization?.scheduling_enabled),
  timezone: organization?.timezone || 'UTC',
});

// Scheduling calendars are the premium calendar feature of the organization's tier.
export const useSchedulingAccess = () => useCurrentOrganization(selectSchedulingAccess);

export const useSchedulingProfiles = (enabled = true) =>
  useQuery({
    queryKey: schedulingKeys.profiles,
    queryFn: () => schedulingProfileService.list(),
    enabled,
  });

export const useSchedulingProfile = (id: string) =>
  useQuery({
    queryKey: schedulingKeys.profile(id),
    queryFn: () => schedulingProfileService.get(id),
    enabled: !!id,
  });

export const useCreateSchedulingProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SchedulingProfileInput) => schedulingProfileService.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: schedulingKeys.profiles }),
  });
};

export const useUpdateSchedulingProfile = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SchedulingProfileInput) => schedulingProfileService.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: schedulingKeys.profiles }),
  });
};

// The way out when a profile with bookings cannot be deleted.
export const useDeactivateSchedulingProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => schedulingProfileService.update(id, { is_active: false }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: schedulingKeys.profiles }),
  });
};

export const useDeleteSchedulingProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => schedulingProfileService.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: schedulingKeys.profiles }),
  });
};

export const useResources = (profileId: string) =>
  useQuery({
    queryKey: schedulingKeys.resources(profileId),
    queryFn: () => resourceService.list({ profile: profileId }),
    enabled: !!profileId,
  });

export const useResourceMutations = (profileId: string) => {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: schedulingKeys.resources(profileId) });
  return {
    create: useMutation({
      mutationFn: (data: ResourceInput) => resourceService.create({ ...data, profile: profileId }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, data }: { id: string; data: ResourceInput }) =>
        resourceService.update(id, data),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => resourceService.remove(id),
      onSuccess: invalidate,
    }),
  };
};

export const useServices = (profileId: string) =>
  useQuery({
    queryKey: schedulingKeys.services(profileId),
    queryFn: () => serviceService.list({ profile: profileId }),
    enabled: !!profileId,
  });

export const useServiceMutations = (profileId: string) => {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: schedulingKeys.services(profileId) });
  return {
    create: useMutation({
      mutationFn: (data: ServiceInput) => serviceService.create({ ...data, profile: profileId }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, data }: { id: string; data: ServiceInput }) =>
        serviceService.update(id, data),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => serviceService.remove(id),
      onSuccess: invalidate,
    }),
    createVariant: useMutation({
      mutationFn: (data: ServiceVariantInput) => serviceVariantService.create(data),
      onSuccess: invalidate,
    }),
    updateVariant: useMutation({
      mutationFn: ({ id, data }: { id: string; data: ServiceVariantInput }) =>
        serviceVariantService.update(id, data),
      onSuccess: invalidate,
    }),
    removeVariant: useMutation({
      mutationFn: (id: string) => serviceVariantService.remove(id),
      onSuccess: invalidate,
    }),
  };
};

export type AvailabilityOwner = 'profile' | 'resource';

export const useAvailabilityRules = (owner: AvailabilityOwner, id: string) =>
  useQuery({
    queryKey: schedulingKeys.rules(owner, id),
    queryFn: () => availabilityRuleService.list({ [owner]: id }),
    enabled: !!id,
  });

export const useAvailabilityRuleMutations = (owner: AvailabilityOwner, id: string) => {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: schedulingKeys.rules(owner, id) });
  return {
    create: useMutation({
      mutationFn: (data: AvailabilityRuleInput) =>
        availabilityRuleService.create({ ...data, [owner]: id }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ ruleId, data }: { ruleId: string; data: AvailabilityRuleInput }) =>
        availabilityRuleService.update(ruleId, data),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (ruleId: string) => availabilityRuleService.remove(ruleId),
      onSuccess: invalidate,
    }),
  };
};

export const useAvailabilityExceptions = (owner: AvailabilityOwner, id: string) =>
  useQuery({
    queryKey: schedulingKeys.exceptions(owner, id),
    queryFn: () => availabilityExceptionService.list({ [owner]: id }),
    enabled: !!id,
  });

export const useAvailabilityExceptionMutations = (owner: AvailabilityOwner, id: string) => {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: schedulingKeys.exceptions(owner, id) });
  return {
    create: useMutation({
      mutationFn: (data: AvailabilityExceptionInput) =>
        availabilityExceptionService.create({ ...data, [owner]: id }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (exceptionId: string) => availabilityExceptionService.remove(exceptionId),
      onSuccess: invalidate,
    }),
  };
};

// The calendar view needs the rules and exceptions of the profile and all its
// resources at once. The endpoints are tenant-scoped, the caller filters by owner.
export const useAllAvailabilityRules = () =>
  useQuery({
    queryKey: ['scheduling', 'rules', 'all'],
    queryFn: () => availabilityRuleService.list(),
  });

export const useAllAvailabilityExceptions = () =>
  useQuery({
    queryKey: ['scheduling', 'exceptions', 'all'],
    queryFn: () => availabilityExceptionService.list(),
  });

// Bookings also arrive from the phone agent and the public page, so the calendar polls.
// Polling pauses while the tab is hidden; coming back refreshes right away.
export const BOOKINGS_REFRESH_MS = 20_000;

export const useBookings = (profileId: string, from: string, to: string) =>
  useQuery({
    queryKey: schedulingKeys.bookings(profileId, from, to),
    queryFn: () => bookingService.list(profileId, from, to),
    placeholderData: keepPreviousData,
    enabled: !!profileId,
    staleTime: 0,
    refetchInterval: BOOKINGS_REFRESH_MS,
    refetchOnWindowFocus: true,
  });

// The booking feed shows what the agent booked last, so it polls like the calendar.
export const useBookingList = (query: BookingListQuery) =>
  useQuery({
    queryKey: ['scheduling', 'booking-list', query] as const,
    queryFn: () => bookingService.listPage(query),
    placeholderData: keepPreviousData,
    enabled: !!query.profile,
    staleTime: 0,
    refetchInterval: BOOKINGS_REFRESH_MS,
    refetchOnWindowFocus: true,
  });

export const useBookingMutations = (profileId: string) => {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['scheduling', 'bookings', profileId] });
    queryClient.invalidateQueries({ queryKey: ['scheduling', 'booking-list'] });
  };
  return {
    create: useMutation({
      mutationFn: (data: BookingCreateInput) => bookingService.create(data),
      onSuccess: invalidate,
    }),
    move: useMutation({
      mutationFn: ({ id, data }: { id: string; data: BookingMoveInput }) =>
        bookingService.move(id, data),
      onSuccess: invalidate,
    }),
    cancel: useMutation({
      mutationFn: (id: string) => bookingService.cancel(id),
      onSuccess: invalidate,
    }),
    markNoShow: useMutation({
      mutationFn: (id: string) => bookingService.markNoShow(id),
      onSuccess: invalidate,
    }),
  };
};
